import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import type { ParseKeys } from 'i18next';
import { useWebchatStore } from '@/store/webchat.store';
import { languageName } from '@/utils/language-name';
import styles from './bridge-status-bar.module.scss';

// Dictionary keys, resolved at render time. Keyed by the pipeline status the bridge emits.
const ACTIVITY_LABELS: Record<'visitor' | 'agent', Record<string, ParseKeys<'webchat'>>> = {
  visitor: {
    transcribing: 'bridge.activity.visitor.transcribing',
    translating: 'bridge.activity.visitor.translating',
    speaking: 'bridge.activity.visitor.speaking',
  },
  agent: {
    transcribing: 'bridge.activity.agent.transcribing',
    translating: 'bridge.activity.agent.translating',
    speaking: 'bridge.activity.agent.speaking',
  },
};

interface BridgeStatusBarProps {
  conversationId: number;
  callerLanguage?: string | null;
  agentLanguage?: string | null;
  assignedAgentId?: number | null;
}

export const BridgeStatusBar = ({
  conversationId,
  callerLanguage,
  agentLanguage,
  assignedAgentId,
}: BridgeStatusBarProps) => {
  const { t } = useTranslation('webchat');
  const bridgeStatus = useWebchatStore((s) => s.bridgeStatus[conversationId]);
  const callerLabel = languageName(callerLanguage);
  const agentLabel = languageName(agentLanguage) || t('bridge.statusBar.defaultAgentLanguage');

  // Before the agent claims: listen-only AI handling
  if (!assignedAgentId) {
    return (
      <div
        className={clsx(styles.bar, styles.aiOnly)}
        role="status"
        aria-live="polite"
        data-testid="bridge-status-bar"
        data-bridge-state="ai-only"
      >
        <div className={styles.left}>
          <span className={styles.dot} />
          <span className={styles.primary}>{t('bridge.statusBar.aiHandling')}</span>
        </div>
        <span className={styles.activity}>
          {t('bridge.statusBar.callerLanguage', { language: callerLabel })}
        </span>
      </div>
    );
  }

  // Claimed but bridge hasn't emitted a pipeline event yet
  if (!bridgeStatus) {
    return (
      <div
        className={clsx(styles.bar, styles.connecting)}
        role="status"
        aria-live="polite"
        data-testid="bridge-status-bar"
        data-bridge-state="connecting"
      >
        <div className={styles.left}>
          <span className={styles.dot} />
          <span className={styles.primary}>{t('bridge.statusBar.connecting')}</span>
        </div>
        <span className={styles.activity}>
          {t('bridge.statusBar.callerLanguage', { language: callerLabel })}
        </span>
      </div>
    );
  }

  // Active bridge — show current pipeline activity when either side is busy
  const activityKey =
    ACTIVITY_LABELS.visitor[bridgeStatus.visitor] || ACTIVITY_LABELS.agent[bridgeStatus.agent];
  const activity = activityKey ? t(activityKey) : '';

  return (
    <div
      className={clsx(styles.bar, styles.active)}
      role="status"
      aria-live="polite"
      data-testid="bridge-status-bar"
      data-bridge-state="active"
    >
      <div className={styles.left}>
        <span className={styles.dot} />
        <span className={styles.primary}>
          {t('bridge.statusBar.translating', { caller: callerLabel, agent: agentLabel })}
        </span>
      </div>
      {activity && <span className={styles.activity}>{activity}</span>}
    </div>
  );
};
